
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { ArticleDto } from "../../dtos/articleDto";
import { ArticleState } from "../../dtos/articleState";
import httpClient from "../../store/httpClient";
import ArticleCadPreview from "@/components/ArticleCadPreview.vue";
import UpdateBoundingBoxCommand from "./UpdateBoundingBoxCommand.vue";
import { ArticleHelper } from "@/views/Article/ArticleHelper";
import ArticleFaceTypes from "@/dtos/IArticleFaceTypes";
import { IArticleFaceType } from "@/dtos/IArticleFaceTypes";
import PowderFinishes from "@/dtos/IPowderFinish";
import { IPowderFinish } from "@/dtos/IPowderFinish";
import PowderStructures from "@/dtos/IPowderStructure";
import { IPowderStructure } from "@/dtos/IPowderStructure";
import { PriceListDto } from "@/dtos/priceListDto";
import PricelistButtonComponent from "./PricelistButtonComponent.vue";
import { getZincCoatingText } from "@/dtos/zincCoating";
import router from "@/router";

@Component({
	components: {
		ArticleCadPreview,
		UpdateBoundingBoxCommand,
		PricelistButtonComponent,
	},
})
export default class ArticleComponent extends Vue {
	@Prop()
	private article!: ArticleDto;
	private loading: boolean = false;
	private articleDialog: boolean = false;
	private lastSurcharge: number = 0;
	private lastBlexonComment: string = "";
	private lastManufacturingComment: string = "";
	private errorMessage: string = "";
	private articleFaces: IArticleFaceType[] = ArticleFaceTypes;
	private powderFinishes: IPowderFinish[] = PowderFinishes;
	private powderStructures: IPowderStructure[] = PowderStructures;

	private mounted() {
		this.lastSurcharge = this.article.priceSurcharge;
		this.lastBlexonComment = this.article.blexonComment;
		this.lastManufacturingComment = this.article.manufacturingComment;
		this.$store.dispatch("loadMissingSubArticles", this.article.subArticleIds);
	}

	private get subarticles(): ArticleDto[] {
		const subarticles = this.$store.state.articles.filter((a: ArticleDto) =>
			this.article.subArticleIds.some((id) => id == a.articleId)
		);
		return subarticles;
	}

	@Watch("subarticles")
	private replaceAssembliesWithOnlyOneSubarticle() {
		if (this.article.subArticleIds.length === 1 && this.subarticles.length === 1) {
			// The assembly has one subarticle and the subarticle has been loaded
			this.$store.commit("replaceAssemblyBySubarticle", this.article);
		}
	}

	private get weight(): number {
		let total = 0;
		this.subarticles.map(
			(a) => (total += (a.quantityInAssembly == null ? 0 : a.quantityInAssembly) * a.weight)
		);
		return total;
	}

	private get assemblyState() {
		const locked: boolean =
			this.article.locked || this.subarticles.some((a: ArticleDto) => a.locked);
		const canBeProduced: boolean =
			this.article.canBeProduced &&
			this.subarticles.every((a: ArticleDto) => a.canBeProduced);
		let state = ArticleState.OK;
		if (
			this.subarticles.length <= 0 ||
			this.subarticles.some((a: ArticleDto) => a.state == ArticleState.Error)
		) {
			state = ArticleState.Error;
		} else if (this.subarticles.some((a: ArticleDto) => a.state == ArticleState.Info)) {
			state = ArticleState.Info;
		} else if (this.subarticles.some((a: ArticleDto) => a.state == ArticleState.Warning)) {
			state = ArticleState.Warning;
		} else if (this.subarticles.every((a: ArticleDto) => a.state == ArticleState.Deleted)) {
			state = ArticleState.Deleted;
		}
		const calculating = this.subarticles.some((a: ArticleDto) => a.calculating);
		return {
			locked: locked,
			state: state,
			calculating: calculating,
			canBeProduced: canBeProduced,
		};
	}

	private get articleStateText() {
		if (this.assemblyState.locked) {
			return "In Überprüfung";
		}
		switch (this.assemblyState.state) {
			case ArticleState.OK: {
				return "OK";
			}
			case ArticleState.Warning: {
				return "Warnung";
			}
			case ArticleState.Error: {
				return "Fehler";
			}
			case ArticleState.Info: {
				return "Aktion erforderlich";
			}
			case ArticleState.Deleted: {
				return "Gelöscht";
			}
			default: {
				return "Unklar";
			}
		}
	}

	private get articleStateColor() {
		switch (this.assemblyState.state) {
			case ArticleState.OK: {
				return "#C8E6C9";
			}
			case ArticleState.Warning: {
				return "#FFF9C4";
			}
			case ArticleState.Error: {
				return "#FFCDD2";
			}
			case ArticleState.Deleted: {
				return "#fdcdff";
			}
			default: {
				return "blue";
			}
		}
	}

	private goToAssemblyView() {
		// Remember the scroll position for when getting back to the article dashboard.
		this.$store.commit("setArticleScrollPosition", window.scrollY);
		router.push(`/article/assembly/${this.article.articleId}`);
	}

	/**
	 * Creates a text to show at the user interface to inform the user
	 * about the weldline features.
	 */
	private getWeldlineString(): string {
		return ArticleHelper.getWeldlineString(this.article);
	}

	private async deleteArticle() {
		try {
			if (window.confirm("Sicher?")) {
				await httpClient().post(`article/delete?articleId=${this.article.articleId}`);
				// Event um den Artikel auch lokal zu löschen
				this.$emit("deleted", this.article.articleId);
			}
		} catch (err) {
			console.error(`Der Artikel konnte nicht gelöscht werden. Error: ${err}`);
		}
	}

	private async downloadOriginalImportFile() {
		this.$store.dispatch("downloadOriginalImportFile", this.article.articleId);
	}

	private async reactivateArticle() {
		if (this.article.state != ArticleState.Deleted) return;
		try {
			this.article.calculating = true;
			await httpClient().post(
				`article/ReactivateArticle?articleId=${this.article.articleId}`,
				null
			);
			this.$store.commit("setSnackbarText", "Artikel erfolgreich reaktiviert.");
			this.$emit("reactivatedArticle", this.article.articleId);
		} catch (err) {
			this.$store.commit("setSnackbarErrorText", "Artikel konnte nicht reaktiviert werden.");
		}
	}

	private async saveEdits() {
		if (this.article.priceSurcharge == null || (this.article.priceSurcharge as any) == "") {
			this.article.priceSurcharge = 0;
		}
		try {
			if (this.lastBlexonComment != this.article.blexonComment) {
				let data: any = {
					comment: this.article.blexonComment,
				};
				await httpClient().post(
					`article/SetBlexonComment?articleId=${this.article.articleId}`,
					data
				);
				this.lastBlexonComment = this.article.blexonComment;
			}
			if (this.lastManufacturingComment != this.article.manufacturingComment) {
				let data: any = {
					comment: this.article.manufacturingComment,
				};
				await httpClient().post(
					`article/SetManufacturingComment?articleId=${this.article.articleId}`,
					data
				);
				this.lastManufacturingComment = this.article.manufacturingComment;
			}
			if (this.lastSurcharge != this.article.priceSurcharge) {
				let data: any = {
					surcharge: this.article.priceSurcharge,
				};
				await httpClient().post(
					`article/SetSurcharge?articleId=${this.article.articleId}`,
					data
				);
				this.lastSurcharge = this.article.priceSurcharge;
			}
			if (this.errorMessage != "") {
				let data: any = {
					comment: this.errorMessage,
				};
				await httpClient().post(
					`article/addHelpdeskMessage?articleId=${this.article.articleId}`,
					data
				);
				this.errorMessage = "";
			}
			// Remove existing price lists so that they're fetched again when requested.
			// Price lists of subarticles are also removed.
			let articleIdsForPriceRemoval = [
				this.article.articleId,
				...this.subarticles.map((sa: ArticleDto) => sa.articleId),
			];
			console.log(
				"Collected article ids to remove price lists for: ",
				articleIdsForPriceRemoval
			);
			this.$store.commit("removeArticlePriceLists", articleIdsForPriceRemoval);
			this.$store.commit("setSnackbarText", "Änderungen wurden erfolgreich gespeichert.");
		} catch (err) {
			this.$store.commit(
				"setSnackbarErrorText",
				"Änderungen konnten nicht korrekt gespeichert werden."
			);
		} finally {
			this.articleDialog = false;
		}
	}

	private async unlockArticle() {
		try {
			const response = await httpClient().post(
				`article/UnlockArticle?articleId=${this.article.articleId}`
			);
			this.article.state = response.data;
			this.article.locked = false;
		} catch (err) {
			console.log(`Der Artikel konnte nicht entsperrt werden. Error: ${err}`);
		}
	}

	private async updateThumbnail() {
		try {
			await httpClient().post(`Article/UpdateThumbnail?articleId=${this.article.articleId}`);
			this.$store.commit("setSnackbarText", "Vorschaubild wird neu berechnet");
		} catch {
			console.log("Update thumbnail failed.");
			this.$store.commit("setSnackbarErrorText", "Update Thumbnail ist fehlgeschlagen.");
		}
	}

	private GetArticleFaceString(enumValue: number) {
		return this.articleFaces.filter((type: IArticleFaceType) => type.value == enumValue)[0]
			.text;
	}
	private GetStructureString(enumValue: number) {
		return this.powderStructures.filter((type: IPowderStructure) => type.value == enumValue)[0]
			.text;
	}
	private GetFinishString(enumValue: number) {
		return this.powderFinishes.filter((type: IPowderFinish) => type.value == enumValue)[0].text;
	}

	private priceListsUpdated(priceLists: PriceListDto[] | null) {
		if (priceLists !== null) {
			this.article.priceLists = priceLists;
		}
	}

	private get assemblyMaterialText(): string {
		if (!this.subarticles || this.subarticles.length <= 0) return "";
		// If some materials or werkstoffe are not defined, print "Divers"
		if (this.subarticles.some((a: ArticleDto) => a.materialId <= 0 || a.werkstoffId <= 0))
			return "Material: Divers";
		// If all articles have the same material (same thickness), print exact material.
		if (
			this.subarticles.every(
				(a: ArticleDto) => a.materialId === this.subarticles[0].materialId
			)
		) {
			return this.subarticles[0].materialname;
		}
		// If all articles have the same werkstoff but not the same thickness, print werkstoff and range of thicknesses.
		if (
			this.subarticles.every(
				(a: ArticleDto) => a.werkstoffId === this.subarticles[0].werkstoffId
			)
		) {
			const werkstoffName = this.subarticles[0].werkstoffname;
			const thicknesses = this.subarticles
				.map((a: ArticleDto) => Math.round(a.thickness * 2) / 2)
				.sort((a, b) => a - b);
			return (
				werkstoffName +
				" " +
				this.formatThickness(thicknesses[0]) +
				" - " +
				this.formatThickness(thicknesses[thicknesses.length - 1]) +
				"mm"
			);
		}
		return "Material: Divers";
	}

	private formatThickness(thickness: number): string {
		return Number.isInteger(thickness) ? thickness.toString() : thickness.toFixed(1);
	}

	private get zincCoatingText(): string {
		return getZincCoatingText(this.article.zincCoating);
	}
}
